import { z } from 'zod';

/**
 * Het platformbeheer zoals het over de lijn komt.
 *
 * Elk schema volgt één API Resource uit `smartkeur-api` (tussen haakjes). De
 * schermen zien deze vorm nooit: `platform-model.ts` maakt er het model van.
 * Een verwijzing is altijd een `*_uuid`; een numerieke sleutel komt niet over de
 * lijn (`02-api-contract.md` §Algemeen, "Identificatie — bindend").
 */

/** ISO-8601 met tijdzone. */
const timestamp = z.string();

/** (`Platform\TenantResource`) */
export const platformTenantResourceSchema = z.object({
    uuid: z.string(),
    name: z.string(),
    slug: z.string(),
    status: z.string(),
    plan_code: z.string().nullable(),
    plan_name: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    locale: z.string().nullable(),
    timezone: z.string().nullable(),
    trial_ends_at: timestamp.nullable(),
    suspended_at: timestamp.nullable(),
    suspended_reason: z.string().nullable(),
    created_at: timestamp.nullable(),
    /** Of nieuwe keuringen stoppen als de keuringen op zijn; alleen het platform zet dit. */
    block_on_empty: z.boolean(),
    /** Hoeveel keuringen het saldo onder nul mag voordat ze stoppen. */
    grace_credits: z.number().int(),
});

export type PlatformTenantResource = z.infer<typeof platformTenantResourceSchema>;

/**
 * (`Tenancy\PlanResource`) een abonnement van de prijzenlijst, `GET /plans`. Bedragen in hele
 * centen, exclusief btw, net als `GET /public/pricing`.
 */
export const planResourceSchema = z.object({
    code: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    monthly_price_cents: z.number(),
    included_inspections: z.number(),
    featured: z.boolean(),
    is_public: z.boolean(),
    sort_order: z.number(),
});

export type PlanResource = z.infer<typeof planResourceSchema>;

/**
 * (`Website\PricingResource`) de hele prijzenlijst, `GET /public/pricing`: dezelfde die de
 * website toont. Bedragen in hele centen, exclusief btw; `vat_rate` is een fractie (0.21).
 */
export const pricingResourceSchema = z.object({
    currency: z.string(),
    vat_rate: z.number(),
    trial_inspections: z.number(),
    extra_inspection_price_cents: z.number(),
    single_report: z.object({ name: z.string(), price_cents: z.number() }),
    plans: z.array(
        z.object({
            code: z.string(),
            name: z.string(),
            description: z.string().nullable(),
            monthly_price_cents: z.number(),
            included_inspections: z.number(),
            featured: z.boolean(),
            sort_order: z.number(),
        }),
    ),
});

export type PricingResource = z.infer<typeof pricingResourceSchema>;

/**
 * Een persoon in een samenvatting. Bij het starten van meekijken stuurt de API
 * hier nog een leeg object (`{}`); daarom mogen beide velden ontbreken.
 */
const personSummarySchema = z.object({
    uuid: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
});

/** (`Platform\ImpersonationSessionResource`) */
export const impersonationSessionResourceSchema = z.object({
    uuid: z.string(),
    reason: z.string(),
    started_at: timestamp,
    expires_at: timestamp,
    ended_at: timestamp.nullable(),
    ended_reason: z.string().nullable(),
    is_active: z.boolean(),
    ip: z.string().nullable(),
    platform_user: personSummarySchema.optional(),
    target_user: personSummarySchema.optional(),
});

export type ImpersonationSessionResource = z.infer<typeof impersonationSessionResourceSchema>;

/** Het antwoord van `POST /platform/tenants/{tenant}/impersonate` (202). */
export const impersonationTicketResourceSchema = z.object({
    token: z.string(),
    expires_at: timestamp,
    session: impersonationSessionResourceSchema,
});

export type ImpersonationTicketResource = z.infer<typeof impersonationTicketResourceSchema>;

/** (`Platform\CreditAdjustmentResource`) */
export const creditAdjustmentResourceSchema = z.object({
    transaction_uuid: z.string(),
    amount: z.number(),
    balance_after: z.number(),
    was_replayed: z.boolean(),
});

export type CreditAdjustmentResource = z.infer<typeof creditAdjustmentResourceSchema>;

/**
 * (`Platform\PlatformMetricResource`) één dag van één bedrijf. Een getal dat een
 * context niet kon leveren is `null`: dat is "onbekend", niet nul.
 */
export const platformMetricResourceSchema = z.object({
    uuid: z.string().nullable().optional(),
    tenant_uuid: z.string().nullable().optional(),
    date: z.string(),
    orders_created: z.number().nullable(),
    inspections_completed: z.number().nullable(),
    reports_sent: z.number().nullable(),
    average_lead_time_minutes: z.number().nullable(),
    credits_consumed: z.number().nullable(),
    active_users: z.number().nullable(),
    is_complete: z.boolean(),
    captured_at: timestamp,
});

export type PlatformMetricResource = z.infer<typeof platformMetricResourceSchema>;

/** (`Platform\AuditLogResource`) */
export const auditLogResourceSchema = z.object({
    uuid: z.string().nullable(),
    tenant_uuid: z.string().nullable(),
    log_name: z.string().nullable(),
    event: z.string().nullable(),
    description: z.string(),
    properties: z.record(z.string(), z.unknown()).or(z.array(z.unknown())),
    during_impersonation: z.boolean(),
    subject_type: z.string().nullable(),
    subject_uuid: z.string().nullable().optional(),
    actor: personSummarySchema.nullable().optional(),
    created_at: timestamp.nullable(),
});

export type AuditLogResource = z.infer<typeof auditLogResourceSchema>;

/* -------------------------------------------------------------------------- */
/* De eigen omgeving: bedrijf, huisstijl, instellingen, mail en domeinen       */
/* -------------------------------------------------------------------------- */

/** (`Tenancy\TenantResource`) het eigen bedrijf, `GET /tenant`. */
export const tenantResourceSchema = z.object({
    uuid: z.string(),
    name: z.string(),
    slug: z.string(),
    legal_name: z.string().nullable(),
    kvk_number: z.string().nullable(),
    vat_number: z.string().nullable(),
    /** Zonder spaties, zoals de API het bewaart; het scherm toont het in groepjes van vier. */
    iban: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    website: z.string().nullable(),
    logo_url: z.string().nullable(),
    address: z.object({
        street: z.string().nullable(),
        number: z.string().nullable(),
        suffix: z.string().nullable(),
        postcode: z.string().nullable(),
        city: z.string().nullable(),
        country: z.string().nullable(),
    }),
    status: z.string(),
    status_label: z.string(),
    locale: z.string().nullable(),
    timezone: z.string().nullable(),
    trial_ends_at: timestamp.nullable(),
    suspended_at: timestamp.nullable(),
    suspended_reason: z.string().nullable(),
    onboarding_completed_at: timestamp.nullable(),
    /**
     * De proef, of `null` voor een bedrijf dat betaalt. `ends_at` is leeg na een aanmelding op
     * de website; `inspections_remaining` alleen voor wie het tegoed mag zien.
     */
    trial: z
        .object({ ends_at: timestamp.nullable(), inspections_remaining: z.number().nullable() })
        .nullable()
        .optional(),
    /** Wat de bezoeker op de prijzenlijst van de website aanklikte; een voorkeur, geen abonnement. */
    plan_preference: z.string().nullable().optional(),
    /** Alleen als de API het abonnement heeft geladen. */
    plan: z.object({ code: z.string(), name: z.string() }).nullable().optional(),
    created_at: timestamp.nullable(),
});

export type TenantResource = z.infer<typeof tenantResourceSchema>;

/**
 * (`Tenancy\TenantBrandingResource`) de huisstijl. Bestanden zijn langlevende,
 * ondertekende links of `null`; een media-sleutel komt nooit over de lijn.
 */
export const tenantBrandingResourceSchema = z.object({
    accent_color: z.string().nullable(),
    /*
     * De twee kleuren naast het accent kwamen later. Een antwoord uit de
     * offline cache van daarvoor heeft ze niet; `null` betekent daar hetzelfde
     * als hier: niet gekozen, dus elk vlak houdt zijn eigen inkt.
     */
    accent_text_color: z.string().nullable().default(null),
    heading_color: z.string().nullable().default(null),
    cover_template: z.string().nullable(),
    report_footer_text: z.string().nullable(),
    logo_url: z.string().nullable(),
    logo_dark_url: z.string().nullable(),
    favicon_url: z.string().nullable(),
    signature_url: z.string().nullable(),
    email_header_url: z.string().nullable(),
    updated_at: timestamp.nullable(),
});

export type TenantBrandingResource = z.infer<typeof tenantBrandingResourceSchema>;

/** (`Tenancy\TenantSettingsResource`) camelCase, precies zoals `TenantSettings` het opslaat. */
export const tenantSettingsResourceSchema = z.object({
    autoSendReport: z.boolean(),
    autoInvoice: z.boolean(),
    sendReminders: z.boolean(),
    reminderDaysBefore: z.number().int(),
    blockOnEmptyCredits: z.boolean(),
    graceCredits: z.number().int(),
    lowCreditThreshold: z.number().int(),
    showCostsOnPublicReport: z.boolean(),
    allowForwarding: z.boolean(),
    requireAccessCode: z.boolean(),
    reportLinkValidDays: z.number().int(),
    openingHours: z.record(z.string(), z.string().nullable()),
    /*
     * De NHG-instellingen kwamen later. Een antwoord uit de offline cache van
     * daarvoor heeft ze niet; dan geldt wat de server als standaard heeft.
     */
    includeNhgAppendix: z.boolean().default(true),
    /** Een van de vier inschrijvingen van het NHG-model (`NhgRegistration`), of `null`. */
    nhgRegisteredAs: z.string().nullable().default(null),
    /** Het standaard eigen bericht in de mail met rapport en factuur; kwam later dan de rest. */
    invoiceWithReportMessage: z.string().nullable().default(null),
    /** Een rapport dat vanzelf gaat, neemt de factuur mee; kwam later dan de rest. */
    invoiceWithReport: z.boolean().default(false),
});

export type TenantSettingsResource = z.infer<typeof tenantSettingsResourceSchema>;

/** (`Tenancy\TenantMailSettingsResource`) zonder wachtwoord: dat komt nooit terug. */
export const mailSettingsResourceSchema = z.object({
    driver: z.string(),
    driver_label: z.string(),
    host: z.string().nullable(),
    port: z.number().int().nullable(),
    username: z.string().nullable(),
    encryption: z.string().nullable(),
    from_name: z.string().nullable(),
    from_address: z.string().nullable(),
    reply_to_address: z.string().nullable(),
    verified_at: timestamp.nullable(),
    last_error: z.string().nullable(),
    uses_platform_fallback: z.boolean(),
    updated_at: timestamp.nullable(),
});

export type MailSettingsResource = z.infer<typeof mailSettingsResourceSchema>;

/** (`Tenancy\MailDeliveryResource`) de uitkomst van een testbericht. */
export const mailDeliveryResourceSchema = z.object({
    delivered: z.boolean(),
    used_platform_fallback: z.boolean(),
    error: z.string().nullable(),
});

export type MailDeliveryResource = z.infer<typeof mailDeliveryResourceSchema>;

/**
 * (`setup` in `Tenancy\TenantDomainResource`) hoe ver een eigen domein is. Zonder Forge-, domein-
 * of certificaatnummers: het bedrijf ziet stappen, een reden en het ene record dat het zelf zet.
 */
export const domainSetupResourceSchema = z.object({
    /** Het platform zet het domein zelf klaar; anders gebeurt het met de hand. */
    automatic: z.boolean(),
    /** `verification` · `manual` · `server` · `dns` · `certificate` · `checking` · `active` */
    step: z.string(),
    step_label: z.string(),
    /** Gestopt bij `step`; `reason` zegt waarom. */
    failed: z.boolean(),
    reason: z.string().nullable(),
    /** Alleen bij `dns`: wanneer het platform de DNS weer bekijkt. */
    next_check_at: timestamp.nullable(),
    can_retry: z.boolean(),
    dns_record: z
        .object({
            type: z.string(),
            name: z.string(),
            value: z.string(),
        })
        .nullable(),
});

/** (`Tenancy\TenantDomainResource`) */
export const tenantDomainResourceSchema = z.object({
    uuid: z.string(),
    hostname: z.string(),
    type: z.string(),
    type_label: z.string(),
    is_primary: z.boolean(),
    is_verified: z.boolean(),
    verified_at: timestamp.nullable(),
    ssl_status: z.string(),
    ssl_status_label: z.string(),
    /** Alleen zolang het domein nog niet geverifieerd is. */
    verification: z
        .object({
            txt_name: z.string(),
            txt_record: z.string(),
            well_known_path: z.string(),
            token: z.string(),
        })
        .optional(),
    setup: domainSetupResourceSchema,
    created_at: timestamp.nullable(),
});

export type TenantDomainResource = z.infer<typeof tenantDomainResourceSchema>;

/** (`Media\MediaResource`) alleen wat de huisstijl nodig heeft: de identiteit. */
export const uploadedMediaResourceSchema = z.object({ uuid: z.string() });

/* -------------------------------------------------------------------------- */
/* Platformbeheer: aanmaken en uitrollen                                       */
/* -------------------------------------------------------------------------- */

/** (`Platform\ProvisionedTenantResource`) het antwoord van `POST /platform/tenants` (201). */
export const provisionedTenantResourceSchema = z.object({
    tenant: platformTenantResourceSchema,
    owner: z.object({
        /** Leeg zolang de eigenaar zijn uitnodiging nog niet heeft geaccepteerd. */
        uuid: z.string(),
        name: z.string(),
        email: z.string(),
        was_existing_user: z.boolean(),
        invitation_sent: z.boolean(),
    }),
    starting_credits: z.number().int(),
    templates: z.array(z.object({ uuid: z.string(), name: z.string() })),
});

export type ProvisionedTenantResource = z.infer<typeof provisionedTenantResourceSchema>;

/** (`Platform\PlatformTemplateResource`) een vragenlijst die het platform levert. */
export const platformTemplateResourceSchema = z.object({
    uuid: z.string(),
    name: z.string(),
    inspection_type: z.string(),
    version_number: z.number().int().nullable(),
    published_at: timestamp.nullable(),
});

export type PlatformTemplateResource = z.infer<typeof platformTemplateResourceSchema>;

/** (`Platform\TemplateRolloutResource`) de uitkomst per bedrijf, in de volgorde van het verzoek. */
export const templateRolloutResourceSchema = z.object({
    uuid: z.string(),
    template_uuid: z.string(),
    template_name: z.string(),
    status: z.string(),
    status_label: z.string(),
    error: z.string().nullable(),
    completed_at: timestamp.nullable(),
});

export type TemplateRolloutResource = z.infer<typeof templateRolloutResourceSchema>;
